import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { SkeletonModule } from 'primeng/skeleton'
import {
  AvailabilityTools,
  SlotTravelInfo,
} from '../../../core/tools/availability-tools'

export interface AvailabilitiesData {
  user_email: string
  user_name: string
  availabilities: {
    available_slots: Array<{ start: string; end: string }>
    busy_slots: Array<{
      title: string
      start: string
      end: string
      type: string
      doNotShift?: boolean
    }>
  }
}

export interface SelectedIntervention {
  startDate: Date | string
  usersTechnicians?: Array<{
    email?: string
    color?: string
    firstname?: string
    lastname?: string
  }>
}

export interface SelectedTechnician {
  email?: string
  firstname?: string
  lastname?: string
  color?: string
}

export interface EmptyDayData {
  date: string // 'YYYY-MM-DD'
  dayLabel: string // 'Lun 15/02'
  slots: Array<{ start: string; end: string }>
  totalAvailableMinutes: number
}

@Component({
  selector: 'app-availabilities-panel',
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule, SkeletonModule],
  templateUrl: './availabilities-panel.component.html',
  styleUrls: ['./availabilities-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilitiesPanelComponent implements OnChanges {
  // Data inputs
  @Input() selectedIntervention: SelectedIntervention | null = null
  @Input() selectedTechnicianDirect: SelectedTechnician | null = null
  @Input() selectedInterventionAvailabilities: AvailabilitiesData | null = null
  @Input() interventionDuration: number = 60

  // Empty days inputs
  @Input() emptyDaysSlots: EmptyDayData[] = []
  @Input() loadingEmptyDays: boolean = false

  // Tab control: parent can request which tab to show
  @Input() initialTab: 'slots' | 'emptyDays' = 'slots'

  // State inputs
  @Input() loadingSelectedInterventionAvailabilities: boolean = false
  @Input() loadingSlotTravelInfo: boolean = false
  @Input() slotTravelInfo: Map<number, SlotTravelInfo> = new Map()

  // Display inputs for technician info
  @Input() technicianColors: Map<string, string> = new Map()
  @Input() technicianInitials: Map<string, string> = new Map()

  // Outputs
  @Output() closePanel = new EventEmitter<void>()
  @Output() slotSelected = new EventEmitter<{
    slot: { start: string; end: string }
    userEmail: string
  }>()
  @Output() emptyDaySlotSelected = new EventEmitter<{
    slot: { start: string; end: string }
    userEmail: string
    date: string // 'YYYY-MM-DD'
  }>()

  // Local state
  showBusySlots = false
  activeTab: 'slots' | 'emptyDays' = 'slots'

  // ----- Public methods for template -----

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTab'] && changes['initialTab'].currentValue) {
      this.activeTab = changes['initialTab'].currentValue
    }
  }

  toggleBusySlots(): void {
    this.showBusySlots = !this.showBusySlots
  }

  setActiveTab(tab: 'slots' | 'emptyDays'): void {
    this.activeTab = tab
  }

  getEmptyDaySlotsChunked(
    day: EmptyDayData
  ): Array<{ start: string; end: string }> {
    return AvailabilityTools.splitSlotsIntoDurationChunks(
      day.slots,
      this.interventionDuration
    )
  }

  getEmptyDaySlotDuration(slot: { start: string; end: string }): number {
    return AvailabilityTools.getSlotDurationMinutes(slot)
  }

  onClose(): void {
    this.closePanel.emit()
  }

  onSlotClick(slot: { start: string; end: string }, userEmail: string): void {
    this.slotSelected.emit({ slot, userEmail })
  }

  onEmptyDaySlotClick(
    slot: { start: string; end: string },
    userEmail: string,
    date: string
  ): void {
    this.emptyDaySlotSelected.emit({ slot, userEmail, date })
  }

  getTechnicianColorByEmail(email: string): string {
    return this.technicianColors.get(email) || '#6366f1'
  }

  getTechnicianInitialsByEmail(email: string): string {
    return this.technicianInitials.get(email) || '?'
  }

  getSlotDurationMinutes(slot: { start: string; end: string }): number {
    return AvailabilityTools.getSlotDurationMinutes(slot)
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes}min`
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h${mins}` : `${hours}h`
  }

  getSlotTravelInfoByOriginalIndex(
    originalIndex: number
  ): SlotTravelInfo | null {
    return this.slotTravelInfo.get(originalIndex) || null
  }

  getSortedAvailableSlots(): {
    slot: { start: string; end: string }
    originalIndex: number
  }[] {
    const slots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
    if (!slots) return []

    // Sort slots by feasibility first
    const sortedSlots = AvailabilityTools.sortSlotsByFeasibility(
      slots,
      this.slotTravelInfo
    )

    // Filter out slots that don't meet constraints
    const minEffectiveTime = this.interventionDuration * 0.8
    return sortedSlots.filter(
      (item: {
        slot: { start: string; end: string }
        originalIndex: number
      }) => {
        const travelInfo = this.slotTravelInfo.get(item.originalIndex)
        // If no travel info, keep the slot (we don't have constraint data)
        if (!travelInfo) return true

        // Check time-of-day constraints
        if (!this.slotMeetsTimeConstraints(item.originalIndex, item.slot))
          return false

        // For slots that need adjustment (SERRÉ), check raw available time
        // because if we shift adjacent events, we can use the full slot
        if (travelInfo.needsAdjustment) {
          // Keep the slot if raw available time is sufficient
          return travelInfo.availableTime >= this.interventionDuration
        }

        // If effectiveAvailableTime is null/undefined, keep the slot
        if (
          travelInfo.effectiveAvailableTime === null ||
          travelInfo.effectiveAvailableTime === undefined
        )
          return true
        // Filter out if effective time is less than 80% of required duration
        return travelInfo.effectiveAvailableTime >= minEffectiveTime
      }
    )
  }

  getFeasibleSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return totalSlots

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      // Skip slots that don't meet all constraints
      if (!this.slotMeetsAllConstraints(i)) continue

      const info = this.slotTravelInfo.get(i)
      // Exclure les shift-slots - ils ont leur propre catégorie "décaler"
      if (info?.requiresShift) continue
      if (!info || info.isFeasible) count++
    }
    return count
  }

  getAdjustableSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return 0

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      // Skip slots that don't meet all constraints
      if (!this.slotMeetsAllConstraints(i)) continue

      // Only count slots that need adjustment AND can actually be adjusted
      if (this.isSlotAdjustable(i)) count++
    }
    return count
  }

  // ----- Private methods -----

  private slotMeetsTimeConstraints(
    slotIndex: number,
    slot: { start: string; end: string }
  ): boolean {
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    if (!travelInfo) return true

    // Check home departure time (for first slot of day)
    if (travelInfo.isFirstSlotOfDay && travelInfo.homeDepartureTime) {
      const departureHour = travelInfo.homeDepartureTime.getHours()
      // Before 7:00 AM - not allowed
      if (departureHour < 7) {
        return false
      }
    }

    // Check home return time using backend-calculated value
    if (travelInfo.isLastSlotOfDay && travelInfo.homeReturnTime) {
      const returnHour = travelInfo.homeReturnTime.getHours()
      const returnMin = travelInfo.homeReturnTime.getMinutes()
      // After 19:30 (7:30 PM) - not allowed
      if (returnHour > 19 || (returnHour === 19 && returnMin > 30)) {
        return false
      }
    }

    // Fallback: if we don't have homeReturnTime but it's the last slot, estimate it
    if (
      travelInfo.isLastSlotOfDay &&
      !travelInfo.homeReturnTime &&
      travelInfo.travelToHome
    ) {
      const slotStart = new Date(slot.start)
      const returnTime = new Date(
        slotStart.getTime() +
          (this.interventionDuration + travelInfo.travelToHome) * 60 * 1000
      )
      const returnHour = returnTime.getHours()
      const returnMin = returnTime.getMinutes()
      if (returnHour > 19 || (returnHour === 19 && returnMin > 30)) {
        return false
      }
    }

    return true
  }

  private slotMeetsAllConstraints(slotIndex: number): boolean {
    const slots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
    if (!slots || !slots[slotIndex]) return true

    const slot = slots[slotIndex]

    // Check time-of-day constraints (departure before 7:00, return after 19:30)
    if (!this.slotMeetsTimeConstraints(slotIndex, slot)) return false

    // Check 80% effective time threshold
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    if (!travelInfo) return true

    // For slots that need adjustment (SERRÉ), check raw available time
    // because if we shift adjacent events, we can use the full slot
    if (travelInfo.needsAdjustment) {
      return travelInfo.availableTime >= this.interventionDuration
    }

    if (
      travelInfo.effectiveAvailableTime === null ||
      travelInfo.effectiveAvailableTime === undefined
    )
      return true
    const minEffectiveTime = this.interventionDuration * 0.8
    return travelInfo.effectiveAvailableTime >= minEffectiveTime
  }

  /**
   * Check if a slot that needs adjustment is actually adjustable
   * A slot is NOT adjustable if it requires shifting an event that has do_not_shift = true
   */
  isSlotAdjustable(slotIndex: number): boolean {
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    if (!travelInfo) return false
    if (!travelInfo.needsAdjustment) return false

    // If the slot needs adjustment, check if adjacent events can be shifted
    // If either adjacent event has do_not_shift = true, the slot is NOT adjustable
    if (travelInfo.previousEventDoNotShift === true) return false
    if (travelInfo.nextEventDoNotShift === true) return false

    return true
  }

  /**
   * Check if a slot is blocked (needs adjustment but cannot be adjusted)
   * because adjacent events have do_not_shift = true
   */
  isSlotBlocked(slotIndex: number): boolean {
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    if (!travelInfo) return false
    if (!travelInfo.needsAdjustment) return false

    // The slot needs adjustment but at least one adjacent event cannot be shifted
    return (
      travelInfo.previousEventDoNotShift === true ||
      travelInfo.nextEventDoNotShift === true
    )
  }

  /**
   * Get count of blocked slots (need adjustment but cannot be adjusted)
   */
  getBlockedSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return 0

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      if (!this.slotMeetsAllConstraints(i)) continue
      if (this.isSlotBlocked(i)) count++
    }
    return count
  }

  /**
   * Check if a slot is a shift-slot (requires moving an existing unlocked intervention)
   */
  isShiftSlot(slotIndex: number): boolean {
    const travelInfo = this.slotTravelInfo.get(slotIndex)
    return travelInfo?.requiresShift === true
  }

  /**
   * Get count of shift-slots
   */
  getShiftSlotsCount(): number {
    const totalSlots =
      this.selectedInterventionAvailabilities?.availabilities?.available_slots
        ?.length || 0

    if (this.slotTravelInfo.size === 0) return 0

    let count = 0
    for (let i = 0; i < totalSlots; i++) {
      if (this.isShiftSlot(i)) count++
    }
    return count
  }
}
